import { call, put, select } from 'redux-saga/effects';
import {
  createBrandedFixtureFailed,
  createBrandedFixtureSuccess,
  fetchBrandedFixtures,
  fetchBrandedFixturesSuccess,
  fetchBrandedFixturesFailed,
  searchBrandedFixture,
  searchBrandedFixtureFailed,
  searchBrandedFixtureSuccess,
  updateBrandedFixtureFailed,
  updateBrandedFixtureSuccess,
  fetchCollectionsSuccess,
  fetchCollectionsFailed,
  fetchVendorCollectionListingsSuccess,
  fetchVendorCollectionListingsFailed,
  fetchOfficeFixturesSuccess,
  fetchOfficeFixturesFailed,
  fetchDefaultOfficeBrandedFixturesSuccess,
  fetchDefaultOfficeBrandedFixturesFailed,
  saveOfficeFixturesSuccess,
  saveOfficeFixturesFailed,
} from './branded-fixtures-actions';
import { getSearch } from './branded-fixtures-selectors';
import { api, showErrorNotification, showSuccessNotification } from '@med-fe/util';

export function* fetchBrandedFixturesStartWorker({ payload }: any): Generator {
  try {
    const url = `/fixtures/search`;
    const searchPayload = {
      vendor: payload?.vendor || null,
      collection: payload?.collection || null,
      genericType: payload?.genericType ?? null,
      fixtureName: payload?.fixtureName || null,
    };
    const data: any = yield call(api.post, url, searchPayload);
    yield put(fetchBrandedFixturesSuccess(Array.isArray(data) ? data : data?.contents || []));
  } catch (e) {
    console.log(e);
    yield put(fetchBrandedFixturesFailed(e));
    showErrorNotification(null, e);
  }
}

export function* searchBrandedFixturesStartWorker({ payload }: any): Generator {
  try {
    const url = `/fixtures/search`;
    // Map frontend search params to API payload
    const searchPayload = {
      vendor: payload?.vendorName || null,
      collection: payload?.collection || null,
      genericType: payload?.isGeneric ?? null,
      fixtureName: payload?.fixtureName || null,
    };

    const data: any = yield call(api.post, url, searchPayload);
    const all: any[] = Array.isArray(data) ? data : [];

    const page = payload?.page ?? 0;
    const size = payload?.size ?? all.length;
    const total = all.length;
    const pages = size ? Math.ceil(total / size) : 0;
    const contents = size ? all.slice(page * size, page * size + size) : [];

    yield put(searchBrandedFixtureSuccess({ contents, pages, total }));
  } catch (e) {
    yield put(searchBrandedFixtureFailed(e));
    showErrorNotification();
  }
}

function* uploadFixturePhotos(brandedFixtureSid: number, photos: File[]): Generator {
  try {
    const formData = new FormData();
    formData.append('brandedFixtureSid', String(brandedFixtureSid));
    photos.forEach((photo) => formData.append('file', photo));
    yield call(api.post, '/fixtures/photos', formData);
  } catch (e) {
    showErrorNotification(null, e);
  }
}

export function* createBrandedFixtureStartWorker({ payload }: any): Generator {
  try {
    const url = '/fixtures';
    const search = yield select(getSearch);
    const { photos = [], ...fixturePayload } = payload;
    const savedFixture: any = yield call(api.post, url, fixturePayload);
    if (photos.length) {
      const brandedFixtureSid = Number(
        savedFixture?.brandedFixtureSid ?? savedFixture?.id ?? savedFixture?.fixture?.brandedFixtureSid ?? savedFixture
      );
      if (brandedFixtureSid > 0) {
        yield call(uploadFixturePhotos, brandedFixtureSid, photos);
      } else {
        showErrorNotification(null, new Error('Fixture saved, but its ID was not returned for photo upload.'));
      }
    }
    showSuccessNotification('Fixture created successfully');
    yield put(createBrandedFixtureSuccess(fixturePayload));
    yield put(searchBrandedFixture(search));
  } catch (e) {
    yield put(createBrandedFixtureFailed(e));
    showErrorNotification(null, e);
  }
}

export function* updateBrandedFixtureStartWorker({ payload }: any): Generator {
  try {
    const url = '/fixtures';
    const search = yield select(getSearch);
    const { photos = [], removedPhotos = [], ...fixturePayload } = payload;
    const savedFixture: any = yield call(api.post, url, fixturePayload);
    if (removedPhotos.length) {
      yield call(api.post, '/fixtures/photos/delete', removedPhotos);
    }
    if (photos.length) {
      const brandedFixtureSid = Number(
        fixturePayload.id ??
          savedFixture?.brandedFixtureSid ??
          savedFixture?.id ??
          savedFixture?.fixture?.brandedFixtureSid ??
          savedFixture
      );
      if (brandedFixtureSid > 0) {
        yield call(uploadFixturePhotos, brandedFixtureSid, photos);
      } else {
        showErrorNotification(null, new Error('Fixture saved, but its ID was not returned for photo upload.'));
      }
    }
    showSuccessNotification('Fixture updated successfully');
    yield put(updateBrandedFixtureSuccess(fixturePayload));
    yield put(searchBrandedFixture(search));
  } catch (e) {
    yield put(updateBrandedFixtureFailed(e));
    showErrorNotification(null, e);
  }
}

export function* fetchCollectionsStartWorker({ payload }: any): Generator {
  try {
    const url = `/collections`;
    const data = yield call(api.get, url);
    yield put(fetchCollectionsSuccess(data));
  } catch (e) {
    console.log(e);
    yield put(fetchCollectionsFailed(e));
    showErrorNotification(null, e);
  }
}

export function* fetchVendorCollectionListingsStartWorker(): Generator {
  try {
    const url = `/vendor-collection-listings`;
    const data = yield call(api.get, url);
    yield put(fetchVendorCollectionListingsSuccess(data));
  } catch (e) {
    console.log(e);
    yield put(fetchVendorCollectionListingsFailed(e));
    showErrorNotification(null, e);
  }
}

export function* fetchOfficeFixturesStartWorker({ payload }: any): Generator {
  try {
    const data: any = yield call(api.get, `/fixtures/office/${payload}`);
    yield put(fetchOfficeFixturesSuccess(Array.isArray(data) ? data : data?.contents || []));
  } catch (e) {
    yield put(fetchOfficeFixturesFailed(e));
    showErrorNotification(null, e);
  }
}

export function* fetchDefaultOfficeBrandedFixturesStartWorker({ payload }: any): Generator {
  try {
    const officeNumber = encodeURIComponent(payload);
    const assortmentData: any = yield call(api.get, `/offices/${officeNumber}/assortments`);
    const vendorCollections = Array.isArray(assortmentData?.vendorCollections) ? assortmentData.vendorCollections : [];
    const normalizeText = (value: string) => (value || '').trim().toLowerCase();
    const assortmentVendorCollections = vendorCollections.map((vendorCollection: any) => ({
      vendor: normalizeText(vendorCollection.vendor),
      collection: normalizeText(vendorCollection.collection),
    }));
    const data: any = yield call(api.post, '/fixtures/search', {
      vendor: null,
      collection: null,
      genericType: null,
      fixtureName: null,
    });
    const fixtures = Array.isArray(data) ? data : data?.contents || [];
    const defaultFixtures = fixtures.filter((fixture: any) => {
      const fixtureVendor = normalizeText(fixture?.vendorName || fixture?.vendor);
      const fixtureCollection = normalizeText(fixture?.collection || fixture?.collectionName);

      return assortmentVendorCollections.some(
        ({ vendor, collection }) => fixtureVendor === vendor && fixtureCollection === collection
      );
    });

    yield put(fetchDefaultOfficeBrandedFixturesSuccess(defaultFixtures));
  } catch (e) {
    yield put(fetchDefaultOfficeBrandedFixturesFailed(e));
    showErrorNotification(null, e);
  }
}

export function* saveOfficeFixturesStartWorker({ payload }: any): Generator {
  try {
    const data: any = yield call(api.post, '/fixtures/office', payload);
    yield put(saveOfficeFixturesSuccess(Array.isArray(data) ? data : data?.contents || []));
    showSuccessNotification('Office fixtures saved successfully');
  } catch (e) {
    yield put(saveOfficeFixturesFailed(e));
    showErrorNotification(null, e);
  }
}
